import React from 'react';

import { Alert, Box, Chip, Divider, Stack, Typography } from '@mui/material';

import type { RoomRateOccupancyPayload } from './utils';

export type RoomRateCapacityRow = {
  occupancyPayload?: RoomRateOccupancyPayload;
  roomName: string;
  roomRateId: string;
  roomTypeId: string;
};

interface Props {
  capacityRows: Array<RoomRateCapacityRow>;
  readOnly?: boolean;
}

function getCapacityItems(occupancyPayload: RoomRateOccupancyPayload) {
  return [
    { label: 'Max guests', value: occupancyPayload.max_occupancy },
    { label: 'Max adults', value: occupancyPayload.max_adult_occupancy },
    { label: 'Max children', value: occupancyPayload.max_child_occupancy },
    { label: 'Max infants', value: occupancyPayload.max_infant_occupancy },
    { label: 'Max teenagers', value: occupancyPayload.max_teenager_occupancy },
  ];
}

export default function RoomRateCapacityStep({ capacityRows, readOnly = false }: Props) {
  if (capacityRows.length === 0) {
    return <Alert severity="info">No newly created room rates need capacity applied.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Alert severity={readOnly ? 'success' : 'info'}>
        {readOnly ? 'Capacity has been applied to the matched room rates.' : 'Review capacity before applying it.'}
      </Alert>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        {capacityRows.map((capacityRow, index) => (
          <React.Fragment key={capacityRow.roomRateId}>
            <Box sx={{ px: 2, py: 1.5 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2">{capacityRow.roomName}</Typography>
                {capacityRow.occupancyPayload ? (
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {getCapacityItems(capacityRow.occupancyPayload).map((item) => (
                      <Chip
                        key={item.label}
                        label={`${item.label}: ${item.value}`}
                        size="small"
                        variant="outlined"
                        sx={{ bgcolor: 'background.default' }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="warning.main">
                    No Salesforce room asset capacity found
                  </Typography>
                )}
              </Stack>
            </Box>
            {index < capacityRows.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Box>
    </Stack>
  );
}
